// AF-3.16 — Environment Intelligence. Uses AF-2.9's own real,
// already-persisted knowledge records. UNKNOWN never renders as
// COMPLIANT (spec Section 12) -- there is no client-side governance
// re-evaluation here at all, only display of the backend's own stance/
// crawlability/indexability/AI-accessibility/status fields. No crawling
// is performed or implied; "Last verified" is shown only when the
// backend actually supplied it.
//
// Raw Data Console completeness phase -- Environment Impacts
// (intelligence_environment_impacts, EnvironmentImpactService) is a
// real, distinct collection this page never showed: it records when a
// change to an environment-knowledge rule genuinely affects an
// opportunity/recommendation/action_plan/etc, never a mutation of the
// affected object itself. `environmentAPI.impacts()` already existed in
// intelligenceApi.js with zero caller anywhere in the frontend until
// now -- no new API wrapper, no new backend route.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { GovernanceBadge, GovernanceNote, StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { useBrandContext } from '@/hooks/useBrandContext';
import { environmentAPI } from '@/lib/intelligenceApi';

export default function EnvironmentIntelligencePage() {
  const ctx = useBrandContext();
  const [records, setRecords] = useState([]);
  const [impacts, setImpacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!ctx.workspaceId) return;
    setLoading(true); setError(null);
    Promise.all([
      environmentAPI.list(ctx.workspaceId),
      environmentAPI.impacts(ctx.workspaceId).catch(() => ({ data: { impacts: [] } })),
    ])
      .then(([envRes, impactRes]) => {
        setRecords(envRes.data?.environment_knowledge || []);
        setImpacts(impactRes.data?.impacts || []);
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.workspaceId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div data-testid="environment-intelligence-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Environment Intelligence</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Platform policy, citation, crawlability, and AI-accessibility evidence Martian actually has on file. Nothing here is live-crawled.</p>
        </div>

        <BrandContextBar ctx={ctx} />

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && records.length === 0 && (
          <EmptyState title="NO ENVIRONMENT KNOWLEDGE YET" description="Martian has no recorded platform/policy knowledge for this workspace yet." />
        )}

        {!loading && !error && records.length > 0 && (
          <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-card)] overflow-x-auto" data-testid="environment-table">
            <Table>
              <TableHeader>
                <TableRow className="border-[var(--border)] hover:bg-transparent">
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Platform</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Rule Type</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Stance</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Crawlability</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Indexability</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">AI Accessibility</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Status</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Confidence</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Last Verified</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((rec) => (
                  <TableRow key={rec.id} className="border-[var(--border)]" data-testid="environment-row">
                    <TableCell className="text-[11px] text-[var(--text-primary)]">{rec.platform || '—'}</TableCell>
                    <TableCell className="text-[10px] text-[var(--text-secondary)] font-mono">{rec.rule_type}</TableCell>
                    <TableCell>
                      <GovernanceBadge value={rec.stance} />
                      <GovernanceNote value={rec.stance} />
                    </TableCell>
                    <TableCell><StatusBadge value={rec.crawlability} /></TableCell>
                    <TableCell><StatusBadge value={rec.indexability} /></TableCell>
                    <TableCell><StatusBadge value={rec.ai_accessibility} /></TableCell>
                    <TableCell><StatusBadge value={rec.status} /></TableCell>
                    <TableCell><ConfidenceIndicator value={rec.confidence} /></TableCell>
                    <TableCell className="text-[10px] font-mono text-[var(--text-tertiary)]" data-testid="environment-last-verified">
                      {rec.last_verified_at ? new Date(rec.last_verified_at).toLocaleDateString() : 'Not verified'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {!loading && !error && (
          <div className="mt-6">
            <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Environment Impacts</p>
            {impacts.length === 0 ? (
              <EmptyState
                testId="environment-impacts-empty"
                title="NO ENVIRONMENT IMPACTS YET"
                description="Martian has not recorded any opportunity/recommendation/action-plan affected by an environment-knowledge change for this workspace yet."
              />
            ) : (
              <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-card)] overflow-x-auto" data-testid="environment-impacts-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--border)] hover:bg-transparent">
                      <TableHead className="text-[10px] text-[var(--text-tertiary)]">Impact Type</TableHead>
                      <TableHead className="text-[10px] text-[var(--text-tertiary)]">Status</TableHead>
                      <TableHead className="text-[10px] text-[var(--text-tertiary)]">Affected Object</TableHead>
                      <TableHead className="text-[10px] text-[var(--text-tertiary)]">Reason</TableHead>
                      <TableHead className="text-[10px] text-[var(--text-tertiary)]">Confidence</TableHead>
                      <TableHead className="text-[10px] text-[var(--text-tertiary)]">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {impacts.map((im) => (
                      <TableRow key={im.id} className="border-[var(--border)]" data-testid="environment-impact-row">
                        <TableCell className="text-[11px] text-[var(--text-primary)] font-mono">{im.impact_type ?? '—'}</TableCell>
                        <TableCell><StatusBadge value={im.impact_status} /></TableCell>
                        <TableCell className="text-[10px] text-[var(--text-secondary)]">
                          {im.affected_object_type ?? '—'}
                          <div className="font-mono text-[var(--text-tertiary)] break-all">{im.affected_object_id ?? '—'}</div>
                        </TableCell>
                        <TableCell className="text-[11px] text-[var(--text-secondary)] max-w-xs">{im.reason ?? '—'}</TableCell>
                        <TableCell><ConfidenceIndicator value={im.confidence} /></TableCell>
                        <TableCell className="text-[10px] font-mono text-[var(--text-tertiary)]">
                          {im.created_at ? new Date(im.created_at).toLocaleDateString() : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
